/*
 * lazy-slider-init.js
 * Defers connectedCallback() of slider custom elements until they are near the viewport.
 * Above-fold and just-below-fold elements init immediately (no behavior change).
 * Far-below-fold elements wait until they enter a 600px rootMargin window.
 *
 * 2026-07-16: also gates init on theme.css being APPLIED (window.__ukOnCssReady,
 * defined by the watchdog in snippets/head.liquid). theme.css loads async
 * (media="print" + onload) while this JS runs on defer, so Flickity could win the
 * race and measure the unstyled layout — freezing wrong inline heights/positions
 * that persist after the CSS lands (intermittent giant-image / collapsed-carousel
 * bug). With the gate, sliders always measure a styled layout; the watchdog's
 * 8s failsafe guarantees the callback always fires even if theme.css never loads.
 *
 * Targets: <grid-slider>, <slideshow-component>
 *
 * Must load BEFORE theme.js and slideshow-component.js (the scripts that define those tags).
 */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window) || !('customElements' in window)) return;

  var SLIDER_TAGS = ['grid-slider', 'slideshow-component'];
  var ROOT_MARGIN = '600px 0px';
  var IMMEDIATE_THRESHOLD = 800;

  var origDefine = customElements.define.bind(customElements);

  function whenCssReady(cb) {
    if (typeof window.__ukOnCssReady === 'function') {
      window.__ukOnCssReady(cb);
    } else {
      cb();
    }
  }

  customElements.define = function (name, ctor, opts) {
    if (SLIDER_TAGS.indexOf(name) !== -1 && ctor && ctor.prototype && typeof ctor.prototype.connectedCallback === 'function') {
      var origConnected = ctor.prototype.connectedCallback;
      var origDisconnected = ctor.prototype.disconnectedCallback;

      ctor.prototype.connectedCallback = function () {
        var self = this;

        // Un connect ya encolado a la espera del CSS cubre cualquier
        // disconnect/reconnect intermedio (evita doble origConnected).
        if (self.__ukCssQueued) return;
        self.__ukCssQueued = true;

        whenCssReady(function () {
          self.__ukCssQueued = false;
          if (!self.isConnected) return;

          var rect;
          try { rect = self.getBoundingClientRect(); } catch (e) { rect = null; }
          var vh = window.innerHeight || document.documentElement.clientHeight || 0;

          if (!rect || rect.top < vh + IMMEDIATE_THRESHOLD) {
            return origConnected.call(self);
          }

          self.__lazyObserver = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                if (self.__lazyObserver) {
                  self.__lazyObserver.disconnect();
                  self.__lazyObserver = null;
                }
                origConnected.call(self);
                return;
              }
            }
          }, { rootMargin: ROOT_MARGIN });
          self.__lazyObserver.observe(self);
        });
      };

      ctor.prototype.disconnectedCallback = function () {
        if (this.__lazyObserver) {
          this.__lazyObserver.disconnect();
          this.__lazyObserver = null;
        }
        if (typeof origDisconnected === 'function') {
          return origDisconnected.apply(this, arguments);
        }
      };
    }
    return origDefine(name, ctor, opts);
  };
})();
